import React, { useState, useEffect, useRef } from 'react';
import { X, Camera, Plus, Trash2, MapPin, AlertCircle } from 'lucide-react';
import { setActiveVisitId, flushGpsQueue } from '../../lib/gpsTracker';
import { safeFetch, extractError } from '../../lib/safeFetch';
import { compressImage } from '../../lib/imageCompression';
import { getPreciseLocation } from '../../lib/preciseLocation';
import SetSupplierLocationModal from './SetSupplierLocationModal';
import SupplierAutocomplete from './SupplierAutocomplete';
import GateOtpModal from './GateOtpModal';
import LocationVerifyingModal from './LocationVerifyingModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StartVisit({ onClose }) {
  const [suppliers, setSuppliers] = useState([]);
  const [supplierIds, setSupplierIds] = useState([]); // multi-supplier
  const [pickerValue, setPickerValue] = useState('');
  const [locationTarget, setLocationTarget] = useState(null); // supplier currently being pinned
  const [formData, setFormData] = useState({
    purpose: '',
    vehicle_km: '',
    latitude: 0,
    longitude: 0,
    vehicle_type: 'own'
  });
  const [photoBase64, setPhotoBase64] = useState(null);
  const [photoGps, setPhotoGps] = useState(null);
  const [loading, setLoading] = useState(false);
  const [otpModal, setOtpModal] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSuppliers();
    getCurrentLocation();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/suppliers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSuppliers(data);
    } catch (err) {
      console.error('Failed to fetch suppliers:', err);
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData(prev => ({
            ...prev,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }));
        },
        () => alert('Please enable location')
      );
    }
  };

  const handlePhotoCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        setPhotoGps({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          timestamp: new Date().toISOString()
        });
      });
    }
    const compressed = await compressImage(file);
    setPhotoBase64(compressed);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (supplierIds.length === 0) {
      alert('Please add at least one supplier');
      return;
    }
    // Block start until every picked supplier has a mapped location.
    const missingLoc = supplierIds.filter(id => !suppliers.find(s => s.id === id)?.has_location);
    if (missingLoc.length) {
      alert(
        `Please set a location for the supplier${missingLoc.length > 1 ? 's' : ''}:\n\n` +
        missingLoc.map(id => `• ${supplierName(id)}`).join('\n') +
        `\n\nTap the red "Set Location" button next to each supplier.`
      );
      return;
    }
    if (!photoBase64) {
      alert('Please capture vehicle KM photo');
      return;
    }
    const claimNote = formData.vehicle_type === 'own'
      ? 'Own vehicle — expense claim allowed.'
      : 'Company vehicle — NO expense claim.';
    if (!window.confirm(`Start visit?\n\n${claimNote}\nKM: ${formData.vehicle_km}\nSuppliers: ${supplierIds.length}`)) {
      return;
    }
    if (loading) return;
    setLoading(true);

    // Take 5 GPS samples for tight inside/outside-geofence validation.
    setVerifying({ action: 'Visit — START', taken: 0, lastAccuracy: null });
    let preciseLoc;
    try {
      preciseLoc = await getPreciseLocation({
        onProgress: (taken, _t, last) =>
          setVerifying((v) => v && { ...v, taken, lastAccuracy: last?.accuracy ?? v.lastAccuracy }),
      });
    } catch (err) {
      alert(err?.message || 'Location required');
      setVerifying(null); setLoading(false); return;
    }
    setVerifying(null);

    const token = localStorage.getItem('token');
    const result = await safeFetch(`${API_URL}/api/visits/start`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        supplier_ids: supplierIds,
        purpose: formData.purpose,
        vehicle_type: formData.vehicle_type,
        latitude: preciseLoc.latitude,
        longitude: preciseLoc.longitude,
        samples: preciseLoc.samples,
        vehicle_km: parseFloat(formData.vehicle_km),
        photo_base64: photoBase64,
        photo_gps_lat: photoGps?.latitude,
        photo_gps_lon: photoGps?.longitude,
        photo_timestamp: photoGps?.timestamp
      })
    });

    if (result.ok) {
      if (result.data?.visit_id) {
        // Tag every subsequent GPS ping with this visit's id.
        setActiveVisitId(result.data.visit_id);
        // Flush any points already queued from before the start (best-effort).
        flushGpsQueue().catch(() => {});
      }
      try { window.dispatchEvent(new Event('carryover:refresh')); } catch {}
      const mov = result.data?.gate_movement;
      if (mov?.entry_otp) {
        setOtpModal({
          otp: mov.entry_otp,
          label: 'Visit — OUT',
          subtitle: 'Visit started',
          autoApproved: mov.auto_approved,
        });
      } else {
        onClose();
      }
    } else {
      // Cloudflare/WebView sometimes eats the JSON body on 400s. If we still
      // don't have a useful `detail`, poll once for "is there already an
      // ongoing visit?" and show the user a precise message instead of the
      // generic "(400)".
      const detail = (result.data?.detail || result.text || '').toString().toLowerCase();
      if (result.status === 400 && !detail) {
        try {
          const token = localStorage.getItem('token');
          const r = await fetch(`${API_URL}/api/visits/active`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (r.ok) {
            const d = await r.json();
            if (d?.visit?.id) {
              alert('You already have an ongoing visit. Please End it first, then start a new one.');
              setActiveVisitId(d.visit.id);
              setLoading(false);
              return;
            }
          }
          // No ongoing visit — likely the check-in expired.
          alert('Could not start the visit. Please make sure you are checked in and have at least one supplier selected.');
          setLoading(false);
          return;
        } catch (_) {
          // fall through to the generic alert below
        }
      }
      alert(extractError(result, 'Failed to start visit'));
    }
    setLoading(false);
  };

  const addSupplier = () => {
    if (!pickerValue) return;
    if (supplierIds.includes(pickerValue)) {
      alert('Supplier already added');
      return;
    }
    setSupplierIds([...supplierIds, pickerValue]);
    setPickerValue('');
  };

  const removeSupplier = (id) => setSupplierIds(supplierIds.filter(s => s !== id));
  const supplierName = (id) => suppliers.find(s => s.id === id)?.name || id;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-end sm:items-center justify-center" data-testid="start-visit-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-6 pb-28 sm:pb-6 max-h-[92vh] overflow-y-auto overscroll-contain">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">Start New Visit</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">To Suppliers <span className="text-xs text-gray-500">(add one or more)</span></label>
            <div className="flex space-x-2">
              <div className="flex-1">
                <SupplierAutocomplete
                  options={suppliers}
                  value={pickerValue}
                  onChange={setPickerValue}
                  excludeIds={supplierIds}
                  placeholder={`Type to search ${suppliers.length} suppliers…`}
                  testId="start-supplier-autocomplete"
                />
              </div>
              <button
                type="button"
                onClick={addSupplier}
                disabled={!pickerValue}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 flex items-center space-x-1"
                data-testid="start-supplier-add-btn"
              >
                <Plus size={16} /><span className="text-sm">Add</span>
              </button>
            </div>
            {supplierIds.length > 0 && (
              <ul className="mt-2 space-y-1" data-testid="start-supplier-list">
                {supplierIds.map((id, idx) => {
                  const s = suppliers.find(x => x.id === id);
                  const hasLoc = !!s?.has_location;
                  const canEditLoc = !!s?.can_edit_location;
                  return (
                    <li key={id} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg text-sm gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                              hasLoc ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                            }`}
                            title={hasLoc ? 'Location saved' : 'No location — please set before starting'}
                            data-testid={`supplier-gps-pill-${id}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${hasLoc ? 'bg-emerald-500' : 'bg-red-500 animate-pulse'}`} />
                            GPS
                          </span>
                          <span className="truncate"><span className="text-gray-400">#{idx + 1}</span> {supplierName(id)}</span>
                        </div>
                        {!hasLoc && (
                          <p className="text-[11px] text-red-600 mt-0.5 flex items-center gap-1">
                            <AlertCircle size={10} />
                            Set location to start visit.
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {canEditLoc && (
                          <button
                            type="button"
                            onClick={() => setLocationTarget(s)}
                            className={`flex items-center gap-1 text-[11px] px-2 py-1 rounded-md ${
                              hasLoc
                                ? 'text-blue-700 hover:bg-blue-50 border border-blue-200'
                                : 'bg-red-600 text-white hover:bg-red-700'
                            }`}
                            data-testid={`supplier-set-location-${id}`}
                          >
                            <MapPin size={12} />
                            {hasLoc ? 'Edit' : 'Set Location'}
                          </button>
                        )}
                        <button type="button" onClick={() => removeSupplier(id)} className="text-red-500 hover:text-red-700 p-1" data-testid={`start-supplier-remove-${id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {supplierIds.length === 0 && (
              <p className="text-xs text-gray-400 mt-1">Add at least one supplier. You can add more during the trip.</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Purpose (max 15 chars)</label>
            <input
              data-testid="start-purpose-input"
              type="text"
              maxLength={15}
              value={formData.purpose}
              onChange={(e) => setFormData({...formData, purpose: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg"
              placeholder="e.g., Demo, Follow-up"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Vehicle</label>
            <div className="grid grid-cols-2 gap-2" data-testid="vehicle-type-toggle">
              <button
                type="button"
                onClick={() => setFormData({...formData, vehicle_type: 'own'})}
                className={`px-3 py-2.5 rounded-lg border-2 text-sm font-medium ${
                  formData.vehicle_type === 'own' ? 'bg-green-50 border-green-500 text-green-700' : 'bg-white border-gray-200 text-gray-600'
                }`}
                data-testid="vehicle-own-btn"
              >
                🛵 Own Vehicle <span className="text-xs block">(claim allowed)</span>
              </button>
              <button
                type="button"
                onClick={() => setFormData({...formData, vehicle_type: 'company'})}
                className={`px-3 py-2.5 rounded-lg border-2 text-sm font-medium ${
                  formData.vehicle_type === 'company' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-gray-200 text-gray-600'
                }`}
                data-testid="vehicle-company-btn"
              >
                🚐 Company <span className="text-xs block">(no claim)</span>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Vehicle KM Reading</label>
            <input
              data-testid="start-vehicle-km-input"
              type="number"
              step="0.1"
              value={formData.vehicle_km}
              onChange={(e) => setFormData({...formData, vehicle_km: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg"
              placeholder="12345.5"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Vehicle KM Photo (live, GPS-tagged)</label>
            <input
              data-testid="start-photo-input"
              type="file" accept="image/*" capture="environment"
              ref={fileInputRef}
              onChange={handlePhotoCapture}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:bg-gray-50"
              data-testid="start-photo-capture-btn"
            >
              <Camera size={20} />
              <span>{photoBase64 ? 'Photo Captured ✓' : 'Capture KM Photo'}</span>
            </button>
            {photoBase64 && (
              <img src={photoBase64} alt="KM" className="mt-2 max-h-24 rounded-lg" />
            )}
            {photoGps && (
              <p className="text-xs text-gray-500 mt-1">
                GPS: {photoGps.latitude.toFixed(6)}, {photoGps.longitude.toFixed(6)}
              </p>
            )}
          </div>

          <button
            data-testid="start-visit-submit-btn"
            type="submit"
            disabled={loading || !photoBase64 || supplierIds.some(id => !suppliers.find(s => s.id === id)?.has_location)}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50"
          >
            {loading ? 'Starting...' : 'Start Visit'}
          </button>
        </form>
      </div>

      {locationTarget && (
        <SetSupplierLocationModal
          supplier={locationTarget}
          onClose={() => setLocationTarget(null)}
          onSaved={(updated) => {
            // Patch the supplier in local state so the pill turns green immediately.
            setSuppliers(prev => prev.map(s => s.id === updated.id
              ? { ...s, location: updated.location, has_location: true, location_set_by: updated.location_set_by }
              : s
            ));
          }}
        />
      )}

      {verifying && (
        <LocationVerifyingModal action={verifying.action} taken={verifying.taken} lastAccuracy={verifying.lastAccuracy} />
      )}

      {otpModal && (
        <GateOtpModal
          otp={otpModal.otp}
          label={otpModal.label}
          subtitle={otpModal.subtitle}
          autoApproved={otpModal.autoApproved}
          onClose={() => { setOtpModal(null); onClose(); }}
        />
      )}
    </div>
  );
}
